import type { TokenPair } from '@t4s/api-client/model'
import {
  type ApiFailure,
  currentCredentialEpoch,
  failureFromThrown,
  noteCredentialReplaced,
  noteRefreshRunning,
} from './api-error.ts'
import type { PlatformAuthGateway } from './gateway.ts'

/**
 * The platform-admin session: two tokens, **in memory and nowhere else**.
 *
 * docs/DECISIONS.md, "Platform-admin tokens live in memory only, and the session ends
 * with the tab": no `localStorage`, no `sessionStorage`, no cookie this app sets. A
 * reload costs one sign-in, which for a tool opened twice a month is a fair price for a
 * stolen browser profile being worth nothing. **MF29** is the old web app keeping both
 * tokens in `localStorage`, where any script on the origin can read them and where they
 * survive the person walking away.
 *
 * Three consequences this class is responsible for:
 *
 *   - **Restoring a session is re-reading it, not re-loading a token.** Nothing is
 *     persisted, so a cold load has no session and lands on `/login`; a store that still
 *     holds tokens (a remount, a client-side navigation) confirms them against
 *     `GET /platform/auth/session` rather than trusting what it holds.
 *   - **An expired access token refreshes silently, once**, and the refresh runs through
 *     the transport's recovery hook — which structurally cannot re-enter itself
 *     (`packages/api-client/src/fetch.ts`, **CF2**).
 *   - **Thirty minutes idle ends the session**, server-side as well as here. The blast
 *     radius of this session is every tenant in the estate; an unattended laptop is the
 *     likeliest way in (§4.4).
 */

/** Why a session ended, which is the line `/login` shows afterwards (§4.4, §18.2). */
export type SessionEndReason = 'idle' | 'expired' | 'signedOut'

export type SessionSnapshot = {
  /** Present exactly while there is a session. Never written anywhere but memory. */
  readonly tokens: TokenPair | undefined
  /** Set by the end that produced the current signed-out state; cleared once shown. */
  readonly endedReason: SessionEndReason | undefined
}

const SIGNED_OUT: SessionSnapshot = { tokens: undefined, endedReason: undefined }

/** Thirty minutes without an interaction, per §4.4. */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000

/** The one status a revocation can recover from, by rotating the pair it was given. */
const UNAUTHORIZED = 401

export type PlatformSessionStoreOptions = {
  readonly gateway: PlatformAuthGateway
  /** Overridable so a test can drive it, and so the shell can turn it off in a test. */
  readonly idleTimeoutMs?: number
}

export class PlatformSessionStore {
  readonly #gateway: PlatformAuthGateway
  readonly #idleTimeoutMs: number
  readonly #listeners = new Set<() => void>()
  #snapshot: SessionSnapshot = SIGNED_OUT
  #idleTimer: ReturnType<typeof setTimeout> | undefined
  /** True while `#revoke` owns the pair it captured. See `refresh`. */
  #revoking = false
  /** The refresh in flight, settled with whether the pair was rotated. Never rejects. */
  #refreshing: Promise<boolean> | undefined

  constructor(options: PlatformSessionStoreOptions) {
    this.#gateway = options.gateway
    this.#idleTimeoutMs = options.idleTimeoutMs ?? IDLE_TIMEOUT_MS
  }

  /** `useSyncExternalStore`'s half of the contract. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener)
    return () => {
      this.#listeners.delete(listener)
    }
  }

  /** The current state. Referentially stable until something changes it. */
  getSnapshot = (): SessionSnapshot => this.#snapshot

  isSignedIn(): boolean {
    return this.#snapshot.tokens !== undefined
  }

  /**
   * The credential headers the transport asks for before every request. Empty while
   * signed out, so a request made in that state carries no stale token.
   */
  authorizationHeaders = (): Readonly<Record<string, string>> => {
    const tokens = this.#snapshot.tokens
    return tokens === undefined ? {} : { Authorization: `Bearer ${tokens.accessToken}` }
  }

  /**
   * Signs in, and on success starts the session. A refusal throws `ApiFailure` and
   * changes nothing — in particular it does not clear an existing session, because the
   * screen that can reach this while signed in does not exist.
   */
  async signIn(credentials: { nickname: string; password: string }): Promise<void> {
    const tokens = await this.#gateway.signIn(credentials)
    this.#set({ tokens, endedReason: undefined })
    this.noteActivity()
  }

  /**
   * The transport's 401 recovery: rotate the pair once, or end the session.
   *
   * Returns whether the original request should be replayed. It never throws: the
   * transport treats a throw as "not recovered" anyway, and swallowing it here keeps the
   * reason for the failure in one place.
   */
  refresh = async (): Promise<boolean> => {
    // A revocation in flight is already rotating the pair it captured, and a platform
    // refresh token is **single use** — presenting one twice is treated as a stolen
    // token and ends the whole session. So while the session is being revoked the shared
    // recovery stands aside rather than racing it for the same token; there is nothing
    // it could recover into, since whatever it produced would be thrown away a moment
    // later by the end that is already under way.
    const refreshToken = this.#revoking ? undefined : this.#snapshot.tokens?.refreshToken
    if (refreshToken === undefined) {
      return false
    }

    // Every request that *starts* while this runs carries the token being replaced, and the
    // transport gives it no recovery (see `ApiFailure.startedDuringRefreshAt`). Saying so here is
    // what lets `judgeUnauthorized` treat its 401 as the stale answer it is.
    noteRefreshRunning(true)
    const running = this.#rotate(refreshToken)
    this.#refreshing = running
    try {
      return await running
    } finally {
      this.#refreshing = undefined
      noteRefreshRunning(false)
    }
  }

  async #rotate(refreshToken: string): Promise<boolean> {
    const before = this.#snapshot.tokens
    try {
      const tokens = await this.#gateway.refresh(refreshToken)
      // The session ended (a sign-out, the idle clock, another 401) while the refresh was out:
      // what it brings back is thrown away rather than writing tokens into a signed-out store,
      // which would sign the owner back in behind the sign-in page.
      if (this.#snapshot.tokens !== before) {
        return false
      }
      this.#set({ tokens, endedReason: undefined })
      return true
    } catch (thrown) {
      // A spent, revoked or absolutely-expired refresh token is the session ending, not
      // a transient failure — except when the request never reached the API at all, in
      // which case signing the owner out would lose their work over a dropped
      // connection.
      //
      // Only while this refresh still owns the session, as on the success path: a refusal that
      // arrives after the session ended (the idle clock, a sign-out) must not overwrite why it
      // ended, and one that arrives after a *new* sign-in is about a token pair that session
      // never held and must not end it.
      const failure = failureFromThrown(thrown)
      if (failure.status !== 0 && this.#snapshot.tokens === before) {
        this.end('expired')
      }
      return false
    }
  }

  /**
   * What a session-ending 401 that reached the application means — the one decision behind
   * "your session ended" (§4.4, §18.2) — made here rather than at the call site because only the
   * store knows whether a refresh is under way.
   *
   * **A 401 that survived the transport's recovery is the session ending**, and this ends it,
   * at once, exactly as before. The exception is a request that *started while a refresh was
   * running* (`ApiFailure.startedDuringRefreshAt`): it carried the token being replaced and the
   * transport cannot recover it (it is indistinguishable from the refresh call itself, which
   * must never recover — **CF2**), so its 401 arrives whether or not the session is alive. Such
   * a failure waits for the refresh to settle and is judged by what it did: the credential was
   * replaced since the request started, so the answer was about a token no longer in use and
   * the session carries on (`carryOn` lets the caller ask again); the refresh failed, or there
   * was no refresh to wait for and nothing changed, so the 401 stands and the session ends.
   * See `fix-console-refresh-redirect-to-login`.
   */
  judgeUnauthorized(failure: ApiFailure, carryOn: () => void): void {
    if (!this.isSignedIn()) {
      return
    }
    const startedAt = failure.startedDuringRefreshAt
    if (startedAt === undefined) {
      this.end('expired')
      return
    }
    const decide = () => {
      if (!this.isSignedIn()) {
        return
      }
      if (currentCredentialEpoch() === startedAt) {
        this.end('expired')
        return
      }
      carryOn()
    }
    if (this.#refreshing === undefined) {
      decide()
    } else {
      void this.#refreshing.then(decide)
    }
  }

  /**
   * Ends the session **server-side** and then here — §4.4, "It revokes the session
   * server-side, not only in the browser".
   *
   * The local end does not *depend* on the revocation: a sign-out that failed because
   * the API was unreachable must still sign the owner out of this browser. It does wait
   * for it, so that Sign out stays inert until its own work is finished (§5.3, **CF3**).
   */
  async signOut(): Promise<void> {
    await this.#revoke(this.#snapshot.tokens)
    this.end('signedOut')
  }

  /** Ends the session here, recording why, and stops the idle clock. */
  end(reason: SessionEndReason): void {
    this.#clearIdleTimer()
    this.#set({ tokens: undefined, endedReason: reason })
  }

  /**
   * Forgets why the last session ended, once `/login` has said so. Without this the line
   * would reappear on every later visit to the sign-in page.
   */
  clearEndReason(): void {
    if (this.#snapshot.endedReason !== undefined) {
      this.#set({ ...this.#snapshot, endedReason: undefined })
    }
  }

  /**
   * Restarts the idle clock. The shell calls this on real interaction; a request the
   * owner did not cause must not count as one.
   */
  noteActivity(): void {
    this.#clearIdleTimer()
    if (!this.isSignedIn()) {
      return
    }

    this.#idleTimer = setTimeout(() => {
      // Idle is a sign-out, so it revokes too: the session would otherwise stay good for
      // its whole absolute lifetime on a laptop nobody is sitting at. The local end does
      // not wait for it — an unreachable API must not keep somebody signed in — and the
      // pair is handed over on this line, so the ordering is a preference rather than a
      // load-bearing accident.
      //
      // The access token in that pair is **always** expired by now: it lives fifteen
      // minutes and this fires at thirty. So the revocation only ever succeeds because
      // `#revoke` can rotate the pair it was given; see there.
      void this.#revoke(this.#snapshot.tokens)
      this.end('idle')
    }, this.#idleTimeoutMs)
  }

  /** Releases the idle timer. For a test, and for a shell that is unmounting. */
  dispose(): void {
    this.#clearIdleTimer()
  }

  /**
   * Ends `tokens`' session server-side, rotating the pair once if it has to.
   *
   * **The whole pair is a parameter**, captured by the caller before the local session
   * is torn down. Nothing here reads the store: the idle path starts this and then ends
   * the session on the next line, so a revocation that re-read the store — or let the
   * transport read it on the way past — would go out unauthenticated the moment anything
   * downstream became one `await` longer, be refused, and be swallowed below.
   *
   * **An access token alone is not enough to sign out with.** It lives fifteen minutes
   * (`ACCESS_TOKEN_TTL_MS`) and `POST /platform/auth/logout` verifies it like any other
   * route, so a sign-out later than that in a console that makes no other requests —
   * which is every idle timeout, since that fires at thirty minutes, and any deliberate
   * sign-out after a quarter of an hour of reading — is refused. The transport's shared
   * recovery cannot help: the credential is pinned per request, and by the idle path's
   * second line the store holds no refresh token to rotate. So the recovery is here,
   * against the captured pair, and it is the only reason a session ends server-side at
   * all in the common case.
   *
   * One retry, never a loop: a second refusal means the session is already gone, or the
   * refresh token is spent too, and either way there is nothing left to revoke. For the
   * duration, `refresh` above stands aside, so the captured pair is this method's alone.
   */
  async #revoke(tokens: TokenPair | undefined): Promise<void> {
    if (tokens === undefined) {
      return
    }

    this.#revoking = true
    try {
      await this.#revokeWithRotation(tokens)
    } finally {
      this.#revoking = false
    }
  }

  async #revokeWithRotation(tokens: TokenPair): Promise<void> {
    try {
      await this.#gateway.revoke(tokens.accessToken)
      return
    } catch (thrown) {
      if (failureFromThrown(thrown).status !== UNAUTHORIZED) {
        // Unreachable, or refused for a reason a fresher token cannot mend. The local
        // end is what the owner asked for and must happen regardless.
        return
      }
    }

    try {
      // The rotated pair is used and dropped rather than stored: this session is being
      // torn down, and writing tokens back would resurrect one the idle path has already
      // ended.
      const rotated = await this.#gateway.refresh(tokens.refreshToken)
      await this.#gateway.revoke(rotated.accessToken)
    } catch {
      // Already revoked, spent, or unreachable. An error here would only stop the local
      // end happening, which is the one thing that must not depend on the network.
    }
  }

  #clearIdleTimer(): void {
    if (this.#idleTimer !== undefined) {
      clearTimeout(this.#idleTimer)
      this.#idleTimer = undefined
    }
  }

  #set(snapshot: SessionSnapshot): void {
    if (snapshot.tokens !== this.#snapshot.tokens) {
      noteCredentialReplaced()
    }
    this.#snapshot = snapshot
    for (const listener of this.#listeners) {
      listener()
    }
  }
}
